import type { ApiClient } from './client';
import type { RankId } from '@kolony/shared';

// The ladder's client surface (H14). Mirrors `SocialApi`: a thin typed wrapper, no state.

/** A guest rank record as the server issues it. Opaque to us — we store and present it. */
export interface GuestRankRecord {
  guestId: string;
  season: string;
  rating: number;
  rd: number;
  ratedGames: number;
  issuedAt: number;
  sig: string;
}

/**
 * `GET /me/rating`. `rating`/`rd`/`tier` are ABSENT until the player is placed — the server
 * withholds them rather than sending nulls, so `placed` is the only thing worth branching on.
 */
export interface MyRating {
  guest: boolean;
  displayName: string;
  season: string;
  placed: boolean;
  ratedGames: number;
  placementGames: number;
  accountId?: string;
  rating?: number;
  rd?: number;
  provisional?: boolean;
  tier?: RankId;
}

export interface SettleResult {
  settled: boolean;
  games?: number;
  placementGames?: number;
  record?: GuestRankRecord;
  ratedGames?: number;
  reason?: string;
}

export interface ClaimResult {
  claimed: boolean;
  ratedGames?: number;
  placed?: boolean;
  placementGames: number;
  rating?: number;
  tier?: RankId;
}

/**
 * A row of the season board (D218). `displayName` can be null: the query LEFT-joins, so an
 * entry whose account row has gone is reported rather than silently dropped from a ranking.
 */
export interface LeaderboardRow {
  rank: number;
  accountId: string;
  displayName: string | null;
  rating: number;
  rd: number;
  gamesRated: number;
  provisional: boolean;
  tier: RankId;
  you?: boolean;
}

export interface LeaderboardPage {
  season: string;
  top: LeaderboardRow[];
  /** The requester's ±5 neighbourhood; empty for a guest or anyone unplaced. */
  around: LeaderboardRow[];
  me: LeaderboardRow | null;
}

export class RatingApi {
  constructor(private readonly api: ApiClient) {}

  myRating(): Promise<MyRating> {
    return this.api.get<MyRating>('/me/rating');
  }

  leaderboard(): Promise<LeaderboardPage> {
    return this.api.get<LeaderboardPage>('/leaderboard');
  }

  /**
   * Bank every game this guest has finished and not yet settled. Names no game on purpose —
   * "catch me up" self-heals a browser that was closed at the final whistle.
   */
  settle(guestId: string, record: GuestRankRecord | null): Promise<SettleResult> {
    return this.api.post<SettleResult>('/guest-rank/settle', { guestId, ...(record ? { record } : {}) });
  }

  /** At sign-up: hand the account the rank built as a guest. */
  claim(record: GuestRankRecord): Promise<ClaimResult> {
    return this.api.post<ClaimResult>('/me/rating/claim', record);
  }
}
